import React, { useState, useMemo } from 'react';
import { Button, Input } from '../ui';
import { 
  Printer, 
  Clock, 
  FileCheck, 
  DollarSign, 
  Plus, 
  Trash2, 
  Award, 
  Building2, 
  ShieldCheck, 
  Sliders 
} from 'lucide-react';

interface ActivityItem {
  id: string;
  date: string;
  activity: string;
  hours: number;
  category: string;
}

const DEFAULT_ACTIVITIES: ActivityItem[] = [
  { id: '1', date: '2026-08-14', activity: 'Food Pantry Distribution & Inventory Sorting', hours: 4.5, category: 'Community Outreach' },
  { id: '2', date: '2026-08-28', activity: 'Youth Mentorship & Homework Assistance', hours: 3.0, category: 'Education' },
  { id: '3', date: '2026-09-12', activity: 'Annual Charity 5K Course Marshal & Registration', hours: 5.0, category: 'Event Operations' },
  { id: '4', date: '2026-09-26', activity: 'Community Garden Cleanup & Composting', hours: 3.5, category: 'Environmental' }
];

const PURPOSE_PRESETS = [
  'National Honor Society (NHS)',
  'High School Graduation Service Requirement',
  'College Admissions & Scholarship Verification',
  'Court-Ordered Community Service Compliance',
  'Eagle Scout / Girl Scout Gold Award',
  'Presidential Volunteer Service Award (PVSA)',
  'Corporate Volunteer Match & Grant Reporting'
];

export function VolunteerHourVerification() {
  // Document Type
  const [docFormat, setDocFormat] = useState<'letter' | 'certificate'>('letter');

  // Organization Information
  const [orgName, setOrgName] = useState('Clearwater Community Foundation');
  const [taxId, setTaxId] = useState('501(c)(3) EIN: 84-2910482');
  const [orgAddress, setOrgAddress] = useState('1420 Heritage Way, Suite 300, Austin, TX 78701');
  const [supervisorName, setSupervisorName] = useState('Sarah Jenkins');
  const [supervisorTitle, setSupervisorTitle] = useState('Director of Volunteer Operations');
  const [supervisorEmail, setSupervisorEmail] = useState('s.jenkins@clearwaterfoundation.org');
  const [supervisorPhone, setSupervisorPhone] = useState('(512) 555-0194');

  // Volunteer Information
  const [volunteerName, setVolunteerName] = useState('Maya Lin');
  const [studentId, setStudentId] = useState('NHS-2026-881');
  const [purpose, setPurpose] = useState('National Honor Society (NHS)');
  const [dateRange, setDateRange] = useState('August 1, 2026 – September 30, 2026');
  const [hourlyRate, setHourlyRate] = useState('34.79'); // 2026 Independent Sector US National Average

  // Activity Log
  const [activities, setActivities] = useState<ActivityItem[]>(DEFAULT_ACTIVITIES);

  // New activity input row
  const [newDate, setNewDate] = useState('2026-10-01');
  const [newActivity, setNewActivity] = useState('');
  const [newHours, setNewHours] = useState('2.0');
  const newCategory = 'Community Service';

  // Calculations
  const totalHours = useMemo(() => {
    return activities.reduce((sum, item) => sum + (Number(item.hours) || 0), 0);
  }, [activities]);

  const economicValue = useMemo(() => {
    const rate = parseFloat(hourlyRate) || 0;
    return totalHours * rate;
  }, [totalHours, hourlyRate]);

  const addActivity = () => {
    if (!newActivity.trim()) return;
    const hoursNum = parseFloat(newHours) || 1;
    const newItem: ActivityItem = {
      id: Math.random().toString(36).substring(2, 9),
      date: newDate,
      activity: newActivity.trim(),
      hours: hoursNum,
      category: newCategory.trim() || 'Community Service'
    };
    setActivities(prev => [...prev, newItem]);
    setNewActivity('');
    setNewHours('2.0');
  };

  const removeActivity = (id: string) => {
    setActivities(prev => prev.filter(item => item.id !== id));
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="w-full text-left">
      {/* SCREEN UI - Hidden during print */}
      <div className="print:hidden space-y-8">
        
        {/* Top Header Card */}
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-[3px] border-black dark:border-white bg-[#F5F5F0] dark:bg-[#0A0A0A] shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border-[2px] border-black dark:border-white bg-[#0044FF] text-white flex items-center justify-center font-black">
              <FileCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-black font-display uppercase tracking-tight">
                Volunteer Hour Verification Generator
              </h2>
              <p className="text-xs text-slate-600 dark:text-slate-400 font-mono">
                Generate official signature-ready verification letters & service certificates
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="border-[2px] border-black dark:border-white p-1 bg-white dark:bg-black flex gap-1">
              <button
                type="button"
                onClick={() => setDocFormat('letter')}
                className={`px-3 py-1.5 text-xs font-mono font-black uppercase tracking-wider transition-colors ${
                  docFormat === 'letter'
                    ? 'bg-black text-white dark:bg-white dark:text-black'
                    : 'text-black dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                Official Letter
              </button>
              <button
                type="button"
                onClick={() => setDocFormat('certificate')}
                className={`px-3 py-1.5 text-xs font-mono font-black uppercase tracking-wider transition-colors ${
                  docFormat === 'certificate'
                    ? 'bg-black text-white dark:bg-white dark:text-black'
                    : 'text-black dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                Service Certificate
              </button>
            </div>

            <Button
              onClick={handlePrint}
              className="bg-[#0044FF] hover:bg-blue-700 text-white font-black uppercase text-xs tracking-wider px-5 py-3 border-[2px] border-black dark:border-white shadow-[3px_3px_0px_#000] flex items-center gap-2 cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              Print / Save PDF
            </Button>
          </div>
        </div>

        {/* Impact Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="border-[3px] border-black dark:border-white p-4 bg-white dark:bg-black shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] flex items-center justify-between">
            <div>
              <p className="text-[10px] font-mono font-black uppercase tracking-widest text-slate-500">
                Total Verified Hours
              </p>
              <p className="text-3xl font-black font-display">{totalHours.toFixed(1)} hrs</p>
            </div>
            <Clock className="w-8 h-8 text-[#0044FF]" />
          </div>

          <div className="border-[3px] border-black dark:border-white p-4 bg-[#FFD600] text-black shadow-[4px_4px_0px_#000] flex items-center justify-between">
            <div>
              <p className="text-[10px] font-mono font-black uppercase tracking-widest text-black/70">
                In-Kind Community Impact
              </p>
              <p className="text-3xl font-black font-display">
                ${economicValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-black" />
          </div>

          <div className="border-[3px] border-black dark:border-white p-4 bg-white dark:bg-black shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] flex items-center justify-between">
            <div>
              <p className="text-[10px] font-mono font-black uppercase tracking-widest text-slate-500">
                Valuation Benchmark
              </p>
              <p className="text-lg font-black font-mono">${hourlyRate}/hr</p>
              <p className="text-[9px] font-mono text-slate-400">Independent Sector 2026 Rate</p>
            </div>
            <ShieldCheck className="w-8 h-8 text-emerald-600" />
          </div>
        </div>

        {/* 2-Column Editor + Live Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Form Controls Column */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Volunteer Details Card */}
            <div className="border-[3px] border-black dark:border-white p-5 bg-white dark:bg-black shadow-[6px_6px_0px_#000] dark:shadow-[6px_6px_0px_#fff] space-y-4">
              <h3 className="text-sm font-black font-mono uppercase tracking-widest border-b-[2px] border-black dark:border-white pb-2 flex items-center gap-2">
                <Sliders className="w-4 h-4 text-[#0044FF]" />
                Volunteer & Purpose
              </h3>

              <div>
                <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                  Volunteer Full Name:
                </label>
                <Input
                  value={volunteerName}
                  onChange={(e) => setVolunteerName(e.target.value)}
                  placeholder="e.g. Maya Lin"
                  className="font-bold border-[2px] border-black dark:border-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                    Student / Volunteer ID:
                  </label>
                  <Input
                    value={studentId}
                    onChange={(e) => setStudentId(e.target.value)}
                    placeholder="e.g. NHS-2026-881"
                    className="font-mono text-xs border-[2px] border-black dark:border-white"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                    Service Date Range:
                  </label>
                  <Input
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    placeholder="e.g. Aug 1 - Sep 30, 2026"
                    className="text-xs border-[2px] border-black dark:border-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                  Purpose / Verification Target:
                </label>
                <Input
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  className="text-xs border-[2px] border-black dark:border-white mb-2"
                />
                <div className="flex flex-wrap gap-1">
                  {PURPOSE_PRESETS.slice(0, 4).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPurpose(p)}
                      className="text-[9px] font-mono font-bold px-2 py-0.5 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      {p.split(' ')[0]} {p.split(' ')[1] || ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Organization & Supervisor Card */}
            <div className="border-[3px] border-black dark:border-white p-5 bg-white dark:bg-black shadow-[6px_6px_0px_#000] dark:shadow-[6px_6px_0px_#fff] space-y-4">
              <h3 className="text-sm font-black font-mono uppercase tracking-widest border-b-[2px] border-black dark:border-white pb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-emerald-600" />
                Certifying Organization
              </h3>

              <div>
                <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                  Organization Name:
                </label>
                <Input
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="font-bold border-[2px] border-black dark:border-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                    Tax Status / EIN:
                  </label>
                  <Input
                    value={taxId}
                    onChange={(e) => setTaxId(e.target.value)}
                    placeholder="e.g. 501(c)(3) EIN"
                    className="text-xs border-[2px] border-black dark:border-white"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                    In-Kind Rate ($/hr):
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                    className="font-mono text-xs border-[2px] border-black dark:border-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-mono font-black uppercase tracking-widest block mb-1">
                  Address / City, State:
                </label>
                <Input
                  value={orgAddress}
                  onChange={(e) => setOrgAddress(e.target.value)}
                  className="text-xs border-[2px] border-black dark:border-white"
                />
              </div>

              <div className="pt-2 border-t-[2px] border-black dark:border-white grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] font-mono uppercase block mb-1">Supervisor Name:</label>
                  <Input
                    value={supervisorName}
                    onChange={(e) => setSupervisorName(e.target.value)}
                    className="text-xs border-[2px] border-black dark:border-white"
                  />
                </div>
                <div>
                  <label className="text-[9px] font-mono uppercase block mb-1">Supervisor Title:</label>
                  <Input
                    value={supervisorTitle}
                    onChange={(e) => setSupervisorTitle(e.target.value)}
                    className="text-xs border-[2px] border-black dark:border-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] font-mono uppercase block mb-1">Email:</label>
                  <Input
                    value={supervisorEmail}
                    onChange={(e) => setSupervisorEmail(e.target.value)}
                    className="text-xs border-[2px] border-black dark:border-white"
                  />
                </div>
                <div>
                  <label className="text-[9px] font-mono uppercase block mb-1">Phone:</label>
                  <Input
                    value={supervisorPhone}
                    onChange={(e) => setSupervisorPhone(e.target.value)}
                    className="text-xs border-[2px] border-black dark:border-white"
                  />
                </div>
              </div>
            </div>

            {/* Activity Table Management Card */}
            <div className="border-[3px] border-black dark:border-white p-5 bg-white dark:bg-black shadow-[6px_6px_0px_#000] dark:shadow-[6px_6px_0px_#fff] space-y-4">
              <h3 className="text-sm font-black font-mono uppercase tracking-widest border-b-[2px] border-black dark:border-white pb-2 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  Service Activity Log ({activities.length})
                </span>
              </h3>

              {/* Add Activity Row */}
              <div className="p-3 border-[2px] border-black dark:border-white bg-[#F5F5F0] dark:bg-slate-900 space-y-2">
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider block">Add Shift / Activity:</span>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-4">
                    <Input
                      type="date"
                      value={newDate}
                      onChange={(e) => setNewDate(e.target.value)}
                      className="text-xs p-1.5"
                    />
                  </div>
                  <div className="col-span-5">
                    <Input
                      value={newActivity}
                      onChange={(e) => setNewActivity(e.target.value)}
                      placeholder="Activity / Role..."
                      className="text-xs p-1.5"
                    />
                  </div>
                  <div className="col-span-3 flex gap-1">
                    <Input
                      type="number"
                      step="0.5"
                      value={newHours}
                      onChange={(e) => setNewHours(e.target.value)}
                      className="text-xs p-1.5 w-16"
                    />
                    <Button
                      onClick={addActivity}
                      className="bg-[#0044FF] text-white p-2 font-black shrink-0"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Activity List */}
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {activities.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-2 border border-slate-300 dark:border-slate-800 text-xs font-mono bg-white dark:bg-black"
                  >
                    <div className="truncate pr-2">
                      <span className="font-bold text-slate-500 mr-2">{item.date}</span>
                      <span className="font-bold">{item.activity}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-black bg-[#FFD600] text-black px-1.5 py-0.5 text-[10px]">
                        {item.hours}h
                      </span>
                      <button
                        type="button"
                        onClick={() => removeActivity(item.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

            </div>

            <Button
              onClick={handlePrint}
              className="w-full bg-[#0044FF] hover:bg-blue-700 text-white font-black uppercase tracking-widest py-3 border-[2px] border-black dark:border-white shadow-[4px_4px_0px_#000] flex items-center justify-center gap-2 cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              Print / Save PDF ({docFormat === 'letter' ? 'Letter' : 'Certificate'})
            </Button>

          </div>

          {/* Live Preview Column */}
          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono font-black uppercase tracking-widest text-slate-500">
                Live Document Preview ({docFormat === 'letter' ? 'Portrait Letter' : 'Landscape Certificate'}):
              </span>
            </div>

            <div className="border-[4px] border-black dark:border-white bg-slate-200 dark:bg-slate-950 p-4 sm:p-8 overflow-x-auto flex justify-center shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff]">
              <div 
                className="transition-transform origin-top scale-[0.5] sm:scale-[0.65] md:scale-[0.8] xl:scale-[0.9]"
                style={{ width: docFormat === 'letter' ? '816px' : '920px', minHeight: docFormat === 'letter' ? '1056px' : '650px' }}
              >
                {docFormat === 'letter' ? (
                  <VerificationLetterDocument
                    orgName={orgName}
                    taxId={taxId}
                    orgAddress={orgAddress}
                    supervisorName={supervisorName}
                    supervisorTitle={supervisorTitle}
                    supervisorEmail={supervisorEmail}
                    supervisorPhone={supervisorPhone}
                    volunteerName={volunteerName}
                    studentId={studentId}
                    purpose={purpose}
                    dateRange={dateRange}
                    activities={activities}
                    totalHours={totalHours}
                    economicValue={economicValue}
                    hourlyRate={hourlyRate}
                  />
                ) : (
                  <VerificationCertificateDocument
                    orgName={orgName}
                    taxId={taxId}
                    volunteerName={volunteerName}
                    studentId={studentId}
                    purpose={purpose}
                    totalHours={totalHours}
                    economicValue={economicValue}
                    dateRange={dateRange}
                    supervisorName={supervisorName}
                    supervisorTitle={supervisorTitle}
                  />
                )}
              </div>
            </div>
          </div>

        </div>

      </div>

      {/* PRINT-ONLY VIEW */}
      <div className="hidden print:block fixed inset-0 m-0 p-0 bg-white z-[99999]">
        <style type="text/css" media="print">
          {`
            @page {
              size: ${docFormat === 'letter' ? 'portrait' : 'landscape'};
              margin: 12mm;
            }
            body {
              margin: 0;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              background-color: white !important;
            }
          `}
        </style>
        <div className="w-full h-full p-4 bg-white">
          {docFormat === 'letter' ? (
            <VerificationLetterDocument
              orgName={orgName}
              taxId={taxId}
              orgAddress={orgAddress}
              supervisorName={supervisorName}
              supervisorTitle={supervisorTitle}
              supervisorEmail={supervisorEmail}
              supervisorPhone={supervisorPhone}
              volunteerName={volunteerName}
              studentId={studentId}
              purpose={purpose}
              dateRange={dateRange}
              activities={activities}
              totalHours={totalHours}
              economicValue={economicValue}
              hourlyRate={hourlyRate}
            />
          ) : (
            <div style={{ width: '1020px', height: '720px' }}>
              <VerificationCertificateDocument
                orgName={orgName}
                taxId={taxId}
                volunteerName={volunteerName}
                studentId={studentId}
                purpose={purpose}
                totalHours={totalHours}
                economicValue={economicValue}
                dateRange={dateRange}
                supervisorName={supervisorName}
                supervisorTitle={supervisorTitle}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Formal Letterhead Component ──────────────────────────────────────────────

interface LetterProps {
  orgName: string;
  taxId: string;
  orgAddress: string;
  supervisorName: string;
  supervisorTitle: string;
  supervisorEmail: string;
  supervisorPhone: string;
  volunteerName: string;
  studentId: string;
  purpose: string;
  dateRange: string;
  activities: ActivityItem[];
  totalHours: number;
  economicValue: number;
  hourlyRate: string;
}

function VerificationLetterDocument(props: LetterProps) {
  const todayFormatted = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="w-full bg-white text-black font-sans p-12 border border-slate-300 shadow-xl box-border leading-normal">
      {/* Formal Letterhead */}
      <div className="border-b-[4px] border-[#0044FF] pb-6 mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-black font-display uppercase tracking-tight text-black">
            {props.orgName || 'Organization Name'}
          </h1>
          <p className="text-xs font-mono font-bold text-slate-600 mt-1">{props.taxId}</p>
          <p className="text-xs text-slate-500 font-sans">{props.orgAddress}</p>
        </div>
        <div className="text-right">
          <div className="inline-block border-[2px] border-black bg-[#FFD600] px-3 py-1 text-[10px] font-mono font-black uppercase tracking-wider mb-2">
            OFFICIAL CERTIFICATION
          </div>
          <p className="text-xs font-mono text-slate-500">{todayFormatted}</p>
        </div>
      </div>

      {/* Recipient / Subject */}
      <div className="mb-6 space-y-1 text-sm font-sans">
        <p className="font-bold">TO: <span className="font-normal">Verification Committee / Reviewing Official</span></p>
        <p className="font-bold">RE: <span className="font-bold underline">Official Verification of Volunteer Service Hours — {props.volunteerName}</span></p>
        {props.studentId && (
          <p className="text-xs font-mono text-slate-500">Candidate / ID Reference: {props.studentId}</p>
        )}
      </div>

      {/* Formal Statement */}
      <div className="space-y-4 text-xs sm:text-sm text-slate-800 leading-relaxed font-sans mb-6">
        <p>
          This letter serves as an official confirmation that <strong>{props.volunteerName}</strong> has actively completed 
          {' '}<strong>{props.totalHours.toFixed(1)} verified hours</strong> of voluntary community service with <strong>{props.orgName}</strong> during the service period of <strong>{props.dateRange}</strong>.
        </p>

        <p>
          These service hours were performed in good standing for the express purpose of fulfilling requirements for <strong>{props.purpose}</strong>. All volunteer activities were conducted without monetary compensation or remuneration.
        </p>

        <div className="border-[2px] border-black bg-slate-50 p-3 my-4 flex items-center justify-between">
          <div>
            <span className="text-[10px] font-mono font-black uppercase tracking-widest text-slate-500 block">
              Economic In-Kind Impact
            </span>
            <span className="text-sm font-bold text-slate-900">
              {props.totalHours.toFixed(1)} Hours × ${props.hourlyRate}/hr (Independent Sector Valuation)
            </span>
          </div>
          <div className="text-right">
            <span className="text-lg font-black font-display text-emerald-700">
              ${props.economicValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Itemized Service Table */}
      <div className="mb-8">
        <h3 className="text-xs font-mono font-black uppercase tracking-widest mb-2 border-b border-black pb-1">
          Itemized Service Log & Shift Details
        </h3>
        <table className="w-full text-left border-collapse border border-black text-xs font-mono">
          <thead>
            <tr className="bg-slate-100 border-b border-black">
              <th className="border-r border-black p-2 w-28">Date</th>
              <th className="border-r border-black p-2">Activity / Project Description</th>
              <th className="border-r border-black p-2 w-32">Category</th>
              <th className="p-2 w-16 text-right">Hours</th>
            </tr>
          </thead>
          <tbody>
            {props.activities.map((act) => (
              <tr key={act.id} className="border-b border-slate-300">
                <td className="border-r border-slate-300 p-2 font-bold">{act.date}</td>
                <td className="border-r border-slate-300 p-2">{act.activity}</td>
                <td className="border-r border-slate-300 p-2 text-[10px] text-slate-600">{act.category}</td>
                <td className="p-2 text-right font-bold">{act.hours.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 font-black border-t-2 border-black">
              <td colSpan={3} className="p-2 text-right uppercase tracking-wider">Total Certified Volunteer Hours:</td>
              <td className="p-2 text-right">{props.totalHours.toFixed(1)} hrs</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Closing & Sign-off */}
      <div className="mt-8 pt-4 border-t border-slate-300 grid grid-cols-2 gap-8 text-xs font-sans">
        <div>
          <p className="mb-8 font-bold">Certified by Authorized Supervisor:</p>
          <div className="border-b-2 border-black w-64 mb-2 pb-1">
            <span className="font-serif italic text-base">{props.supervisorName}</span>
          </div>
          <p className="font-black text-sm">{props.supervisorName}</p>
          <p className="text-slate-600">{props.supervisorTitle}</p>
          <p className="text-slate-500 font-mono text-[11px]">{props.orgName}</p>
        </div>

        <div className="space-y-1 text-right self-end font-mono text-[11px] text-slate-600">
          <p>Contact Phone: {props.supervisorPhone}</p>
          <p>Contact Email: {props.supervisorEmail}</p>
          <p className="pt-2 text-[9px] text-slate-400 uppercase">Document Verification Stamp: ORG-{props.totalHours * 100}-VERIFIED</p>
        </div>
      </div>
    </div>
  );
}

// ─── Certificate Component ────────────────────────────────────────────────────

interface CertProps {
  orgName: string;
  taxId: string;
  volunteerName: string;
  studentId: string;
  purpose: string;
  totalHours: number;
  economicValue: number;
  dateRange: string;
  supervisorName: string;
  supervisorTitle: string;
}

function VerificationCertificateDocument(props: CertProps) {
  return (
    <div className="w-full h-full bg-[#FAF9F5] border-[16px] border-[#0A192F] p-8 box-border flex flex-col justify-between relative shadow-xl text-black select-none">
      <div className="absolute inset-2 border-[2px] border-[#C5A059] pointer-events-none" />

      {/* Top Header */}
      <div className="text-center relative z-10 pt-2">
        <p className="text-xs font-mono font-bold uppercase tracking-[0.3em] text-slate-500">
          {props.orgName}
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl font-black text-[#0A192F] uppercase tracking-wide mt-1">
          Certificate of Service
        </h1>
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C5A059] font-bold mt-1">
          RECOGNITION OF COMMUNITY DEDICATION & CIVIC ENGAGEMENT
        </p>
      </div>

      {/* Middle Recipient & Hours */}
      <div className="text-center my-auto py-4 relative z-10 space-y-3">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-sans">
          THIS OFFICIAL RECOGNITION IS BESTOWED UPON
        </p>
        <h2 className="text-3xl sm:text-4xl font-serif italic text-black border-b-2 border-[#C5A059] inline-block px-12 pb-1">
          {props.volunteerName}
        </h2>
        
        <div className="pt-2">
          <p className="text-xs text-slate-700 max-w-lg mx-auto font-sans leading-relaxed">
            For outstanding service comprising <strong>{props.totalHours.toFixed(1)} verified volunteer hours</strong> in 
            support of community programs and initiatives for <strong>{props.purpose}</strong> during <strong>{props.dateRange}</strong>.
          </p>
        </div>

        <div className="inline-block bg-[#0A192F] text-[#C5A059] font-mono text-xs px-4 py-1.5 uppercase tracking-wider font-bold">
          Verified Total: {props.totalHours.toFixed(1)} Service Hours • In-Kind Value: ${props.economicValue.toFixed(2)}
        </div>
      </div>

      {/* Bottom Signatures & Seal */}
      <div className="flex justify-between items-end px-8 relative z-10 pb-2">
        <div className="w-56 text-center">
          <div className="h-8 flex items-end justify-center pb-1">
            <span className="font-serif italic text-base text-slate-800">{props.supervisorName}</span>
          </div>
          <div className="border-t border-[#C5A059] pt-1">
            <p className="text-xs font-bold text-slate-900">{props.supervisorName}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{props.supervisorTitle}</p>
          </div>
        </div>

        {/* Center Foil Seal */}
        <div className="flex flex-col items-center justify-center">
          <div className="w-16 h-16 rounded-full border-[3px] border-[#C5A059] bg-[#FFEFA6] p-1 flex items-center justify-center shadow-md">
            <div className="w-full h-full rounded-full border border-dashed border-[#8A6D3B] flex flex-col items-center justify-center text-center">
              <Award className="w-4 h-4 text-[#8A6D3B]" />
              <span className="text-[6px] font-black uppercase tracking-widest text-[#5C4033]">VERIFIED</span>
            </div>
          </div>
          <span className="text-[8px] font-mono text-slate-400 mt-1 uppercase">ID: {props.studentId || 'SERVICE'}</span>
        </div>

        <div className="w-56 text-center">
          <div className="h-8 flex items-end justify-center pb-1">
            <span className="font-mono text-xs text-slate-800">
              {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
          <div className="border-t border-[#C5A059] pt-1">
            <p className="text-xs font-bold text-slate-900">Date of Attestation</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{props.taxId}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
